import { STAFF_TYPES, isGmRole, type GmRole, type StaffType } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";

/**
 * The page's side of the lead's staff routes (ADR-112 decision 5.5, P1.9-T207): a POST of `{}` to `POST /staff/accounts`
 * and of `{ family, wears }` to `POST /staff/designation`, each with the page's current session token as a bearer token
 * and no cookie, the answer read field by field. What the page cannot read (a fetch that rejects, a body that is no JSON
 * or out of shape) is a failed request, never an answer. The page never decides who may use them: the gateway refuses any
 * account but a lead whatever the page sends, and the Staff tab is only drawn for an offer that carries the word `staff`.
 */

/**
 * One staff account as the gateway lists it.
 */
export interface StaffRow {
  readonly family: string;
  readonly roles: readonly GmRole[];
  readonly types: readonly StaffType[];
  readonly designated: StaffType | null;
  readonly wears: StaffType;
  readonly hidden: boolean;
}

/**
 * The target's offer after a designation.
 */
export interface StaffDesignated {
  readonly types: readonly StaffType[];
  readonly wears: StaffType;
  readonly hidden: boolean;
}

/**
 * The list as read: the rows, and whether the gateway left staff out for want of room.
 */
export interface StaffListing {
  readonly rows: readonly StaffRow[];
  readonly more: boolean;
}

/**
 * What came of one request: the value; the session ended (401); the roles do not allow it (403); the family is held by no
 * one (404); the type is not one the account holds now (400); over a limit (429); or a failure, the database's (503) or one
 * the page could not read.
 */
export type StaffReply<T> = { readonly kind: "ok"; readonly value: T } | { readonly kind: "session" | "forbidden" | "missing" | "refused" | "rate" | "failed" };

/**
 * The two requests.
 */
export interface StaffClient {
  /**
   * Reads the staff accounts. Never rejects.
   */
  readonly list: () => Promise<StaffReply<StaffListing>>;
  /**
   * Designates the type an account's badge shows, or clears the designation with null. Never rejects.
   */
  readonly designate: (family: string, wears: StaffType | null) => Promise<StaffReply<StaffDesignated>>;
}

const REQUEST_TIMEOUT_MS = 10_000;
const BEARER = "Bearer";
const KINDS: Readonly<Record<number, "session" | "forbidden" | "missing" | "refused" | "rate">> = { 400: "refused", 401: "session", 403: "forbidden", 404: "missing", 429: "rate" };

/**
 * Whether a value is one of the six staff types.
 *
 * @param value The candidate.
 * @returns True for a staff type.
 */
function isType(value: unknown): value is StaffType {
  return typeof value === "string" && (STAFF_TYPES as readonly string[]).includes(value);
}

/**
 * Whether a value is a plain object.
 *
 * @param value The value.
 * @returns True for an object that is not an array or null.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads a row against its shape.
 *
 * @param value The row as sent.
 * @returns The row, or null when a field is missing or of another type.
 */
function rowOf(value: unknown): StaffRow | null {
  if (!isRecord(value)) {
    return null;
  }
  const { family, roles, types, designated, wears, hidden } = value;
  if (typeof family !== "string" || !Array.isArray(roles) || !roles.every((role) => typeof role === "string" && isGmRole(role)) || !Array.isArray(types) || !types.every(isType) || !(designated === null || isType(designated)) || !isType(wears) || typeof hidden !== "boolean") {
    return null;
  }
  return { family, roles: roles as GmRole[], types: [...types], designated, wears, hidden };
}

/**
 * Reads a designation's answer against its shape.
 *
 * @param value The parsed body.
 * @returns The offer, or null when it is out of shape.
 */
function designatedOf(value: unknown): StaffDesignated | null {
  if (!isRecord(value)) {
    return null;
  }
  const { types, wears, hidden } = value;
  return Array.isArray(types) && types.every(isType) && isType(wears) && typeof hidden === "boolean" ? { types: [...types], wears, hidden } : null;
}

/**
 * Builds the client.
 *
 * @param options The gateway's two URLs, the page's current session token (null when it holds none), and the fetch, the
 *   page's own unless a test gives another.
 * @returns The two requests.
 */
export function staffClient(options: { readonly accountsEndpoint: string; readonly designationEndpoint: string; readonly token: () => string | null; readonly fetch?: (input: string, init?: RequestInit) => Promise<Response> }): StaffClient {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));

  /**
   * One POST, its 200 body read by `read`.
   *
   * @param endpoint The URL.
   * @param body The JSON body.
   * @param read Reads the parsed 200 body, null when it is out of shape.
   * @returns The reply.
   */
  const post = async <T>(endpoint: string, body: object, read: (parsed: unknown) => T | null): Promise<StaffReply<T>> => {
    const token = options.token();
    if (token === null || token === "") {
      return { kind: "session" };
    }
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(endpoint, { method: "POST", headers: { Authorization: [BEARER, token].join(" "), "Content-Type": "application/json" }, body: JSON.stringify(body), credentials: "omit", signal: limit.signal });
      if (response.status === 200) {
        let parsed: unknown;
        try {
          parsed = await response.json();
        } catch {
          return { kind: "failed" };
        }
        const value = read(parsed);
        return value === null ? { kind: "failed" } : { kind: "ok", value };
      }
      return { kind: KINDS[response.status] ?? "failed" };
    } catch {
      return { kind: "failed" };
    } finally {
      limit.settle();
    }
  };

  return {
    list: () => post(options.accountsEndpoint, {}, (parsed) => (isRecord(parsed) && Array.isArray(parsed["accounts"]) ? { rows: parsed["accounts"].map(rowOf).filter((row): row is StaffRow => row !== null), more: parsed["more"] === true } : null)),
    designate: (family, wears) => post(options.designationEndpoint, { family, wears }, designatedOf),
  };
}
